/**
 * Imports the necessary React components and libraries for a specific module.
 * @module
 * @requires React
 * @requires Card from "react-bootstrap"
 * @requires Tab from "react-bootstrap/Tab"
 * @requires Tabs from "react-bootstrap/Tabs"
 * @requires Link from "react-router-dom"
 * @requires useTranslation from "react-i18next"
 * @requires CSmartTable from "@coreui/react-pro"
 * @requires CustomToggleButton from "../../components/customToggleButton/customToggleButton"
 * @requires Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb"
 * @requires CustomButton from "../../components/customButton/customButton"
 * @requires ConfirmModal from "../../components/confirmModal/confirmModel"
 * @requires EmptyCell from "../../
 */
import React from "react";
import moment from 'moment-timezone';
import { CSVLink } from "react-csv";
import { Card, Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
// import { CSmartTable } from "@coreui/react-pro";

import Constant from "../../util/constant";
import EmptyCell from "../../components/emptyCell/emptyCell";
import CustomButton from "../../components/customButton/customButton";
import ConfirmModal from "../../components/confirmModal/confirmModel";
import CustomeShimer from "../../components/customeShimer/customeShimer";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
// import CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML";
import CustomToggleButton from "../../components/customToggleButton/customToggleButton";
import CustomeDepotGreyBox from "../../components/customeDepotGreyBox/customeDepotGreyBox";
import SearchBar from "../../components/searchBar/searchBar";

/**
 * A functional component that renders the details of a notice management item.
 * @param {{object}} props - The props passed to the component.
 * @param {{string}} props.innerPagetitle - The title of the inner page.
 * @param {{function}} props.backPageFunction - The function to navigate back to the previous page.
 * @param {{object}} props.smarttableref - The reference to the smart table component.
 * @param {{array}} props.columns - The columns configuration for the smart table.
 * @param {{array}} props.noticeMangmentData - The data to be displayed in the smart table.
 * @param {{number}} props.itemsPerPage - The number of items to display per page in the smart
 */
const NoticeManagementDetailsNewComponent = (props) => {
  const { t } = useTranslation();

  /**
   * Constructs the Cloudinary URL by concatenating the environment variables
   * for the Cloudinary URL, name, and subfolder.
   * @returns The constructed Cloudinary URL.
   */
  const CLOUDINARY_URL =
    process.env.REACT_APP_CLOUDNARY_URL +
    process.env.REACT_APP_CLOUDNARY_NAME +
    process.env.REACT_APP_CLOUDNARY_SUBFLODER;

  /**
   * Destructures the props object and assigns the corresponding values to variables.
   * @param {{object}} props - The props object containing various properties.
   * @returns None
   */
  const {
    noticeDetailData,
    loading,
    innerPagetitle,
    backPageFunction,
    smarttableref,
    columns,
    noticeMangmentData,
    itemsPerPage,
    placeHolderShimer,
    noDataFound,
    dimensions,
    noDataTitle,
    noDataAddButtonTitle,
    noDataDesc,
    emptyDataAddButton,
    clearSearchButton,
    headerHeight,
    breadcrumbHeight,
    tableFilterHeight,
    tabHeaderHeight,
    loaderHtml,
    tableContainerRef,
    noticeTypeContentHeight,
    tabtHeight,
    showDeleteUserModal,
    onDeleteUser,
    closeDeleteUserModal,
    openDeleteUserModal,
    downloadNoticeValue,
    state,
    onCheckAction,
    onHide,
    showStatusModal,
    onProcced,
    closeDeactiveModal,
    isCheckSwitch,
    editNoticeFun,
    loaderButtonShow,
    onTableFilterChange,
    searchValue,
    searchFilterClear,
    exportCsvData,
    onFilteredItemsChange,
    exportCsvHeader,

    depotTypeFilter,
    handlePageClick,
    handleItemsPerPageChange,
    itemsPerPageOptions,
    searchVal,
    searchClearButton,
    onSearchValue,
    totalPages,
    currentPage,
    pagination,
    depotList,
    handleExportClick,
    isExportingData
  } = props;

  /**
   * Retrieves the "item" property from the "state" object and assigns it to the "stateNoticeMagment" variable.
   * @param {{object}} state - The state object.
   * @returns The value of the "item" property from the "state" object.
   */
  const stateNoticeMagment = state?.item;
  const deportNameList = depotList?.map(item => item.name)
  
  const getThumbNailType = (documentUrl) => {
    let subString = documentUrl
      ?.substring(stateNoticeMagment?.documenturl?.lastIndexOf(".") + 1)
      .toUpperCase();

    switch (subString) {
      case "PNG":
        return CLOUDINARY_URL + "placeholder512.png";
      case "PDF":
        return CLOUDINARY_URL + "PDF_150x150.png";
      default:
        return CLOUDINARY_URL + "Logo.png";
    }
  };

  const getDisplayedPages = (currentPage, totalPages) => {
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);
 
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);
 
    // Adjust range if less than maxPagesToShow pages are available
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }
 
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };
  /**
   * Renders a React component that displays the details of a notice.
   * @returns {JSX.Element} - The rendered React component.
   */
  return (
    <>
      <React.Fragment>
        <div className="page-content noticeDetails">
          {/**
           * Renders a Breadcrumbs component with the specified props.
           * @param {{boolean}} breadcrumbMainTitle - Determines whether to display the main title in the breadcrumbs.
           * @param {{boolean}} breadcrumbInner - Determines whether to display the inner breadcrumbs.
           * @param {{string}} innerPagetitle - The title of the inner page.
           * @param {{Function}} backPageFunction - The function to be called when the back button is clicked.
           * @returns The rendered Breadcrumbs component.
           */}
          <Breadcrumbs
            breadcrumbMainTitle={false}
            breadcrumbInner={true}
            innerPagetitle={innerPagetitle}
            backPageFunction={backPageFunction}
          />
          {/**
           * Renders a card component with various details and buttons.
           * @returns The rendered card component.
           */}
          <Card className="noticeContaint">
            <Card.Body className="p-0">
              <div className="main_details_Wrapper">
                <div className="document_image">
                  {placeHolderShimer && <CustomeShimer />}
                  <img
                    src={
                      !stateNoticeMagment?.documentthumb
                        ? getThumbNailType(stateNoticeMagment?.documenturl)
                        : process.env.REACT_APP_AWS_UPLOAD_THUMBNAIL_URL +
                        stateNoticeMagment?.documentthumb
                    }
                    alt="ImageNotFount"
                  />
                </div>
                <div className="document_content">
                  <h1
                    className={`${stateNoticeMagment?.title.length >= 20 && !stateNoticeMagment?.title.includes(' ')
                      ? 'breakeAll'
                      : 'wordBrake'
                      }`}>
                    {" "}
                    {placeHolderShimer && <CustomeShimer />}{" "}
                    {stateNoticeMagment?.title}
                  </h1>
                  <div className="noticeType_Wrapper">
                    <ul className="depotname">
                      {stateNoticeMagment?.depotname?.map((value) => {
                        return (
                          <li
                            className={`${value?.depotname.length >= 20 && !value?.depotname.includes(' ')
                              ? 'breakeAll'
                              : 'wordBrake'} ${deportNameList?.includes(value?.depotname) && 'darkBlue'}`}>
                            {" "}
                            {placeHolderShimer && <CustomeShimer />}{" "}
                            {value?.depotname}
                          </li>
                        );
                      })}
                    </ul>
                    <span
                      className={`${stateNoticeMagment?.noticetypename[0]?.noticetypename.length >= 20 && !stateNoticeMagment?.noticetypename[0]?.noticetypename.includes(' ')
                        ? 'breakeAll noticeType'
                        : 'wordBrake noticeType'
                        }`}>
                      {placeHolderShimer && <CustomeShimer />}
                      {stateNoticeMagment?.noticetypename[0]?.noticetypename}
                    </span>
                  </div>
                  {!stateNoticeMagment?.noticelink
                    ? stateNoticeMagment?.documenturl && (
                      <CustomButton
                        type="submit"
                        title={t("DOWNLOAD")}
                        isLeftIcon={true}
                        icon={<i className="bx bx-import"></i>}
                        className={"Fill_Button"}
                        onClick={() => {
                          downloadNoticeValue(
                            stateNoticeMagment?.documenturl
                          );
                        }}
                      />
                    )
                    : stateNoticeMagment?.noticelink && (
                      <CustomButton
                        type="submit"
                        title={"View Link"}
                        isLeftIcon={true}
                        className={"Fill_Button"}
                        onClick={() => {
                          window.open(stateNoticeMagment?.noticelink);
                        }}
                      />
                    )}
                </div>
                <div className="details_btnWrapper">
                  <div className="status">
                    {placeHolderShimer && <CustomeShimer />}
                    <span>{t("STATUS")}</span>
                    <span className={isCheckSwitch ? "success" : "Deactivated"}>
                      {isCheckSwitch ? t("ACTIVE") : t("DEACTIVATED")}
                    </span>
                  </div>
                  <div className="documentEdit_WrapperButton">
                    {placeHolderShimer && <CustomeShimer />}
                    <CustomToggleButton
                      id=""
                      checked={isCheckSwitch ? true : false}
                      onCheckChange={(e) => {
                        onCheckAction(e, noticeMangmentData);
                      }}
                    />
                    <button className="Edit-Buttton" onClick={editNoticeFun}>
                      <i
                        className="mdi mdi-pencil-outline font-size-24"
                        id="deletetooltip"
                      ></i>
                    </button>
                    <button
                      className="Delete-Buttton"
                      onClick={openDeleteUserModal}
                    >
                      <i
                        className="mdi mdi-Delete-outline font-size-24"
                        id="deletetooltip"
                      ></i>
                    </button>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/**
           * Renders a Card component with multiple tabs and a table.
           * @returns The rendered Card component.
           */}
          <Card
            className={
              loading
                ? "Main_Card Main_Notice_Management_Details_New LoadingTable"
                : "Main_Card Main_Notice_Management_Details_New"
            }
          >
            <Card.Body className="p-0 activeTabSupplier">
              <div className="Main_Card_Body">
                <Tabs
                  defaultActiveKey="report"
                  id="uncontrolled-tab-example"
                  className="mb-3 TabCustome"
                >
                  <Tab eventKey="report" title={t("REPORT")}>
                    <div className="FormButton_Wrapper">
                    <div className="left_div">
                    {/* <SearchBar
                      value={searchVal}
                      placeholder={t("SEARCH_BY_TITLE_SUBTITLE_NOTICE_TYPE")}
                      onChange={(e) => onSearchValue(e.target.value)}
                      onClear={searchClearButton}
                    /> */}

                    <div className="col-sm-auto searchBar">
                      <span
                        className="bx bx-search-alt-2"
                      // onClick={handleSearchClick}
                      >
                      </span>
                      <input
                        className="form-control"
                        type="text"
                        placeholder={t("SEARCH_BY_TITLE_SUBTITLE_NOTICE_DETAILS")}        
                        value={searchValue ? searchValue : ""}
                        onChange={(e) => onSearchValue(e.target.value)}
                        onClear={searchClearButton}
                      />
                      {searchValue && (
                        <>
                          <button className="btn-close" onClick={searchClearButton}></button>
                        </>
                      )}
                    </div>

                  </div>
                  <div className="right_div">                            
                    {placeHolderShimer && <CustomeShimer />}
                          {noticeMangmentData?.length > 0 ?
                              <button
                                  onClick={handleExportClick}
                                  disabled={isExportingData}
                                  className="black-btn Border_Button"
                              >
                                {isExportingData ? 'Preparing Export...' : t("EXPORT")}
                              </button>
                              :
                              <CustomButton className="black-btn Border_Button disableExport">
                                {t("EXPORT")}
                              </CustomButton>
                          }
                          </div>
                        </div>
                    <div className="table-content">
                      <div
                        id="table-scroll"
                        className="table-scroll table-responsive">
                        <table
                          id="main-table"
                          // className="main-table  table management-table NoticeMangmentTable"
                          className={stateNoticeMagment?.signrequired ? "main-table  table management-table noticeMangmentTable" : 'main-table  table management-table noticeMangmentTable noticeMangmentTableSigned'}>
                          <thead className="tableHeading">
                            <tr>
                              <th className="tableHeader" scope="col">
                                <div className="d-inline sortingWrapper">
                                  <div>
                                    {t("USER_DETAILS")}{" "}
                                  </div>
                                </div>
                              </th>
                              <th className="tableHeader" scope="col">
                                {t("DEPOT")}
                              </th>
                              <th className={`tableHeader tableHeader-sortable ${props.sortBy === 'open' ? 'sortable-active' : 'sortable-inactive'}`} scope="col" style={{ cursor: 'pointer' }} onClick={() => props.handleColumnSort('open')} title="Click to sort">
                                <div className="d-inline sortingWrapper">
                                  <span>{t("OPEN")}</span>
                                  {props.sortBy === 'open' ? (
                                    <i className={`bx ${props.sortOrder === 1 ? 'bx-chevron-up' : 'bx-chevron-down'}`}></i>
                                  ) : (
                                    <span className="sort-indicator">↕</span>
                                  )}
                                </div>
                              </th>
                              <th className={`tableHeader tableHeader-sortable ${props.sortBy === 'downloaded' ? 'sortable-active' : 'sortable-inactive'}`} scope="col" style={{ cursor: 'pointer' }} onClick={() => props.handleColumnSort('downloaded')} title="Click to sort">
                                <div className="d-inline sortingWrapper">
                                  <span>{t("DOWNLOADED")}</span>
                                  {props.sortBy === 'downloaded' ? (
                                    <i className={`bx ${props.sortOrder === 1 ? 'bx-chevron-up' : 'bx-chevron-down'}`}></i>
                                  ) : (
                                    <span className="sort-indicator">↕</span>
                                  )}
                                </div>
                              </th>
                              <th className={`tableHeader tableHeader-sortable ${props.sortBy === 'signed' ? 'sortable-active' : 'sortable-inactive'}`} scope="col" style={{ cursor: 'pointer' }} onClick={() => props.handleColumnSort('signed')} title="Click to sort">
                                <div className="d-inline sortingWrapper">
                                  <span>{t("SIGNED")}</span>
                                  {props.sortBy === 'signed' ? (
                                    <i className={`bx ${props.sortOrder === 1 ? 'bx-chevron-up' : 'bx-chevron-down'}`}></i>
                                  ) : (
                                    <span className="sort-indicator">↕</span>
                                  )}
                                </div>
                              </th>
                            </tr>
                            <tr>
                              <th></th>
                              <th>
                                {depotTypeFilter(noticeMangmentData, onTableFilterChange)}
                              </th>
                              <th></th>
                              <th></th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody
                            ref={tableContainerRef}

                            style={{
                              height:
                                noticeMangmentData?.length !== undefined ?
                                  dimensions.height -
                                  headerHeight -
                                  tableFilterHeight -
                                  tabHeaderHeight -



                                  tableFilterHeight -
                                  noticeTypeContentHeight -
                                  (loading && loaderHtml) -
                                  - 300  + "px" 
                                  : // 124 + 'px'
                                  dimensions.height -
                                  headerHeight -
                                  breadcrumbHeight -
                                  tableFilterHeight -
                                  noticeTypeContentHeight -
                                  tabtHeight -
                                  tabHeaderHeight -
                                  200 + 'px',
                              overflow: "auto",
                            }}
                            className="Table-Body-scroll"
                          >
                            {noticeMangmentData?.length > 0 ? (
                              <>
                                {noticeMangmentData.map((item, index) => {
                                  return (
                                    <>
                                      <tr key={`tr${index}`} >
                                        <td
                                          className={
                                            item?.noticestatus ===
                                            Constant.GENERALKEYS.INACTIVE && "disableTd"
                                          }
                                        >
                                          {placeHolderShimer && <CustomeShimer />}
                                          <div className="userDetails">
                                            <p className='wordBrake'>
                                              {item?.fullname}
                                            </p>
                                            <p className='wordBrake'>
                                              {item?.emailid}
                                            </p>
                                            <p className='wordBrake'>
                                              {item?.phone}
                                            </p>
                                          </div>
                                        </td>
                                        <td
                                          className={
                                            item.status ===
                                            Constant.GENERALKEYS.DELETED && "disableTd"
                                          }
                                        >
                                          <p className="counte-depot">
                                            {placeHolderShimer && <CustomeShimer />}
                                            {item?.depotname
                                              ?.slice(0, 2)
                                              .map((value, index) => {
                                                return (
                                                  <>
                                                    <CustomeDepotGreyBox
                                                      className={`${value?.depotname?.length >= 10 && !value?.depotname.includes(' ')
                                                        ? 'breakeAll'
                                                        : 'wordBrake'
                                                        }`}
                                                      depotname={value?.depotname}
                                                    />
                                                  </>
                                                );
                                              })}
                                            {item?.depotname?.length > 2 && (
                                              <CustomeDepotGreyBox
                                                title={item?.depotname
                                                  ?.slice(2)
                                                  .map((valueTilte, valueindex) => {
                                                    return valueTilte.depotname;
                                                  })}
                                                className={`${item?.depotname?.length >= 10 && !item?.depotname?.includes(' ')
                                                  ? 'breakeAll'
                                                  : 'wordBrake'
                                                  }`}
                                                plusShow={true}
                                                depotname={item?.depotname?.length - 2}
                                              />
                                            )}
                                          </p>
                                        </td>

                                        <td
                                          className={
                                            item.status === Constant.GENERALKEYS.DELETED &&
                                            "disableTd"
                                          }
                                        >
                                          <div className="depot-Wrapper">
                                            {placeHolderShimer && <CustomeShimer />}
                                            {!item?.open ? (
                                              <span className="openNull">
                                                <span className="wrong">
                                                  <i className="mdi mdi-close"></i>
                                                </span>
                                              </span>
                                            ) : (
                                              <span className="openRight">
                                                <span className="successRight">
                                                  <i className="dripicons-checkmark"></i>
                                                </span>
                                                {moment(item?.open).format(
                                                  "DD-MM-YYYY h:mm A "
                                                )}
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                        <td
                                          className={
                                            item?.status ===
                                            Constant.GENERALKEYS.DELETED && "disableTd"
                                          }
                                        >
                                          <div className="depot-Wrapper">
                                            {placeHolderShimer && <CustomeShimer />}
                                            {!item?.downloaded ? (
                                              <span className="openNull">
                                                <span className="wrong">
                                                  <i className="mdi mdi-close"></i>
                                                </span>
                                              </span>
                                            ) : (
                                              <span className="openRight">
                                                <span className="successRight">
                                                  <i className="dripicons-checkmark"></i>
                                                </span>
                                                {moment(item?.downloaded).format(
                                                  "DD-MM-YYYY h:mm A "
                                                )}
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                        <td
                                          className={
                                            item?.status ===
                                            Constant.GENERALKEYS.DELETED && "disableTd"
                                          }
                                        >
                                          <div className="depot-Wrapper">
                                            {placeHolderShimer && <CustomeShimer />}
                                            {
                                              stateNoticeMagment?.signrequired ?
                                                !item?.signed ? (
                                                  <span className="openNull">
                                                    <span className="wrong">
                                                      <i className="mdi mdi-close"></i>
                                                    </span>
                                                  </span>
                                                ) : (
                                                  <span className="openRight">
                                                    <span className="successRight">
                                                      <i className="dripicons-checkmark"></i>
                                                    </span>
                                                    {moment(item?.signed).format(
                                                      "DD-MM-YYYY h:mm A "
                                                    )}
                                                  </span>
                                                ) : '-'
                                            }
                                          </div>
                                        </td>
                                      </tr>
                                    </>
                                  );
                                })}
                              </>
                            ) : (
                              <>
                                {!loading && (
                                  <tr className="emptyRow">
                                    <td colSpan={5}>
                                      <EmptyCell
                                        emptyItems={noticeMangmentData}
                                        noDataTitle={
                                          noticeMangmentData?.length === 0
                                            ? noDataFound
                                            : noDataTitle
                                        }
                                        noDataAddButton={false}
                                        clearButton={
                                          searchValue?.length === null ? true : false
                                        }
                                        noDataAddButtonTitle={noDataAddButtonTitle}
                                        noDataDesc={noDataDesc}
                                        emptyDataAddButton={emptyDataAddButton}
                                        clearSearchButton={clearSearchButton}
                                      />
                                    </td>
                                  </tr>
                                )}
                              </>
                            )}
                          </tbody>
                        </table>
                        {noticeMangmentData?.length > 0 && (
                          <div className="paginationDiv mt-4">
                            <nav aria-label="Page navigation">
                              <ul className="pagination">
                                {/* Previous Button */}
                                <li
                            className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              className="page-link placeholder-glow"
                              onClick={() => handlePageClick(currentPage > 0 ? currentPage - 1 : 1)}>
                              <i className="bx bx-chevron-left" />
                            </button>
                          </li>
                          {getDisplayedPages(currentPage, totalPages)?.map((page) => (
                      <li
                        key={page}
                        className={`page-item ${currentPage === page ? "active" : ""}`}
                      >
                        <button
                          className="page-link placeholder-glow"
                          onClick={() => handlePageClick(page)}
                        >
                           {page}
                        </button>
                      </li>
                    ))}
                          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                            <button
                              className="page-link placeholder-glow"
                              onClick={() => handlePageClick(currentPage < totalPages ? currentPage + 1 : totalPages)}>
                              <i className="bx bx-chevron-right" />
                            </button>
                          </li>
                        </ul>
                              <div className="items-per-page-dropdown placeholder-glow">
                                <select
                                  className="form-select"
                                  value={itemsPerPage}
                                  onChange={handleItemsPerPageChange}>
                                  {itemsPerPageOptions.map((option) => (
                                    <option key={option} value={option}>
                                      {option}/ {t("Page")}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </nav>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* <CSmartTable
                      ref={smarttableref}
                      cleaner
                      clickableRows
                      columns={columns}
                      columnFilter={true}
                      columnSorter={true}
                      items={noticeMangmentData}
                      itemsPerPage={itemsPerPage}
                      onFilteredItemsChange={(items) => {
                        onFilteredItemsChange(items);
                      }}

                      scopedColumns={{
                        fullname: (item) => {
                          console.log("item", item);
                          const limitedContent = item?.fullname?.slice(0, 150);
                          return (
                            <>
                              <td
                                className={
                                  item?.status ===
                                  Constant.GENERALKEYS.DELETED && "disableTd"
                                }
                              >
                                <div className="userDetailsWrapper">
                                  <p
                                    className={`${limitedContent.length >= 20 && !limitedContent.includes(' ')
                                      ? 'breakeAll'
                                      : 'wordBrake'
                                      }`}>
                                    {placeHolderShimer && <CustomeShimer />}
                                    {limitedContent}
                                  </p>
                                </div>
                              </td>
                            </>
                          );
                        },
                        emailid: (item) => {
                          return (
                            <>
                              <td
                                className={
                                  item?.status ===
                                  Constant.GENERALKEYS.DELETED && "disableTd"
                                }
                              >
                                <div className="userDetailsWrapper">
                                  <p
                                    className={`${item?.emailid.length >= 20 && !item?.emailid.includes(' ')
                                      ? 'breakeAll'
                                      : 'wordBrake'
                                      }`}>
                                    {placeHolderShimer && <CustomeShimer />}
                                    {item?.emailid}
                                  </p>
                                </div>
                              </td>
                            </>
                          );
                        },
                        phone: (item) => {
                          return (
                            <>
                              <td
                                className={
                                  item?.status ===
                                  Constant.GENERALKEYS.DELETED && "disableTd"
                                }
                              >
                                <div className="userDetailsWrapper">
                                  <p
                                    className={`${item?.phone.length >= 10 && !item?.phone.includes(' ')
                                      ? 'breakeAll'
                                      : 'wordBrake'
                                      }`}>
                                    {placeHolderShimer && <CustomeShimer />}
                                    {item?.phone}
                                  </p>
                                </div>
                              </td>
                            </>
                          );
                        },
                        depotname: (item) => {
                          return (
                            <>
                              <td
                                className={
                                  item.status ===
                                  Constant.GENERALKEYS.DELETED && "disableTd"
                                }
                              >
                                <p className="counte-depot">
                                  {placeHolderShimer && <CustomeShimer />}
                                  {item?.depotname
                                    ?.slice(0, 2)
                                    .map((value, index) => {
                                      return (
                                        <>
                                          <CustomeDepotGreyBox
                                            className={`${value?.depotname.length >= 10 && !value?.depotname.includes(' ')
                                              ? 'breakeAll'
                                              : 'wordBrake'
                                              }`}
                                            depotname={value?.depotname}
                                          />
                                        </>
                                      );
                                    })}
                                  {item?.depotname?.length > 2 && (
                                    <CustomeDepotGreyBox
                                      title={item?.depotname
                                        ?.slice(2)
                                        .map((valueTilte, valueindex) => {
                                          return valueTilte.depotname;
                                        })}
                                      className={`${item?.depotname?.length >= 10 && !item?.depotname?.includes(' ')
                                        ? 'breakeAll'
                                        : 'wordBrake'
                                        }`}
                                      plusShow={true}
                                      depotname={item?.depotname?.length - 2}
                                    />
                                  )}
                                </p>
                              </td>
                            </>
                          );
                        },
                        open: (item) => {
                          return (
                            <td
                              className={
                                item.status === Constant.GENERALKEYS.DELETED &&
                                "disableTd"
                              }
                            >
                              <div className="depot-Wrapper">
                                {placeHolderShimer && <CustomeShimer />}
                                {!item?.open ? (
                                  <span className="openNull">
                                    <span className="wrong">
                                      <i className="mdi mdi-close"></i>
                                    </span>
                                  </span>
                                ) : (
                                  <span className="openRight">
                                    <span className="successRight">
                                      <i className="dripicons-checkmark"></i>
                                    </span>
                                    {moment(item?.open).format(
                                      "DD-MM-YYYY h:mm A "
                                    )}
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        },
                        downloaded: (item) => {
                          return (
                            <td
                              className={
                                item?.status ===
                                Constant.GENERALKEYS.DELETED && "disableTd"
                              }
                            >
                              <div className="depot-Wrapper">
                                {placeHolderShimer && <CustomeShimer />}
                                {!item?.downloaded ? (
                                  <span className="openNull">
                                    <span className="wrong">
                                      <i className="mdi mdi-close"></i>
                                    </span>
                                  </span>
                                ) : (
                                  <span className="openRight">
                                    <span className="successRight">
                                      <i className="dripicons-checkmark"></i>
                                    </span>
                                    {moment(item?.downloaded).format(
                                      "DD-MM-YYYY h:mm A "
                                    )}
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        },
                        signed: (item) => {
                          return (
                            <td
                              className={
                                item?.status ===
                                Constant.GENERALKEYS.DELETED && "disableTd"
                              }
                            >
                              <div className="depot-Wrapper">
                                {placeHolderShimer && <CustomeShimer />}
                                {
                                  stateNoticeMagment?.signrequired ?
                                    !item?.signed ? (
                                      <span className="openNull">
                                        <span className="wrong">
                                          <i className="mdi mdi-close"></i>
                                        </span>
                                      </span>
                                    ) : (
                                      <span className="openRight">
                                        <span className="successRight">
                                          <i className="dripicons-checkmark"></i>
                                        </span>
                                        {moment(item?.signed).format(
                                          "DD-MM-YYYY h:mm A "
                                        )}
                                      </span>
                                    ) : '-'
                                }
                              </div>
                            </td>
                          );
                        },
                      }}
                      tableFilter
                      onTableFilterChange={onTableFilterChange}
                      tableFilterLabel={
                        searchValue?.length > 0 && (
                          <button
                            onClick={searchFilterClear}
                            className="btn-close"
                          ></button>
                        )
                      }
                      tableFilterPlaceholder={t(
                        "SEARCH_BY_TITLE_SUBTITLE_NOTICE_DETAILS"
                      )}
                      tableProps={{
                        className: stateNoticeMagment?.signrequired ? "noticeMangmentTable" : 'noticeMangmentTable noticeMangmentTableSigned',
                        responsive: true,
                        striped: false,
                        hover: false,
                      }}
                      noItemsLabel={
                        !placeHolderShimer && (
                          <EmptyCell
                            emptyItems={noticeMangmentData}
                            noDataTitle={
                              noticeMangmentData?.length === 0
                                ? noDataFound
                                : noDataTitle
                            }
                            noDataAddButton={false}
                            clearButton={
                              searchValue?.length === null ? true : false
                            }
                            noDataAddButtonTitle={noDataAddButtonTitle}
                            noDataDesc={noDataDesc}
                            emptyDataAddButton={emptyDataAddButton}
                            clearSearchButton={clearSearchButton}
                          />
                        )
                      }
                      tableBodyProps={{
                        className: "align-middle Table-Body-scroll",
                        style: {
                          height:
                            noticeMangmentData?.length !== undefined
                              ? dimensions.height -
                              headerHeight -
                              // breadcrumbHeight -
                              tableFilterHeight -
                              noticeTypeContentHeight -
                              // tabtHeight -
                              // tabHeaderHeight -
                              (loading && loaderHtml)
                              : // 124 + 'px'
                              dimensions.height -
                              headerHeight -
                              breadcrumbHeight -
                              tableFilterHeight -
                              noticeTypeContentHeight -
                              tabtHeight -
                              tabHeaderHeight -
                              (loading && loaderHtml) +
                              200 +
                              "px",
                        },
                        ref: tableContainerRef,
                      }}
                    /> */}
                    {/* {console.log(`placeHolderShimer ----->>>>`, placeHolderShimer)} */}
                    {/* {loading && <CustomLoadingHTML />} */}
                  </Tab>
                  <Tab eventKey="notice_details" title={t("NOTICE_DETAILS")}>
                    <div className="noticeTabDetailsWrapper">
                      {stateNoticeMagment?.documenturl && (
                        <div className="detailContent documenturlContent">
                          {placeHolderShimer && <CustomeShimer />}
                          <h5>{t("DOCUMENT")}</h5>
                          <span
                            className={`${stateNoticeMagment?.documenturl?.length >= 10 && !stateNoticeMagment?.documenturl?.includes(' ')
                              ? 'breakeAll document-btnwrapper'
                              : 'wordBrake document-btnwrapper'
                              }`}>
                            {stateNoticeMagment?.documenturl}
                            <CustomButton
                              title={t("VIEW_DOCUMENT")}
                              isLeftIcon={false}
                              className={"Fill_Button"}
                              onClick={() => {
                                window.open(
                                  process.env
                                    .REACT_APP_AWS_UPLOAD_DOCUMENT_URL +
                                  stateNoticeMagment?.documenturl
                                );
                              }}
                            />
                          </span>
                        </div>
                      )}
                      <div className="detailContent">
                        {placeHolderShimer && <CustomeShimer />}
                        <h5>{t("EXPIRY_DATE_AND_TIME")}</h5>
                        <p>
                          {/* {moment(stateNoticeMagment?.expirydatetime)
                            .local()
                            .format("DD MMM YYYY h:mm a")} */}

                          {moment.utc(stateNoticeMagment?.expirydatetime).tz('Europe/London').format('DD MMM YYYY h:mm a')}
                            
                        </p>
                      </div>
                      <div className="detailContent">
                        {placeHolderShimer && <CustomeShimer />}
                        <h5>{t("USER_TO_SIGN_BY")}</h5>
                        <p>
                          {/* {!stateNoticeMagment?.signtill
                            ? "-"
                            : moment(stateNoticeMagment?.signtill)
                              .local()
                              .format("DD MMM YYYY h:mm a") || "-"} */}
                           {!stateNoticeMagment?.signtill
                            ? "-"
                            : moment.utc(stateNoticeMagment?.signtill)
                              .tz('Europe/London')
                              .format("DD MMM YYYY h:mm a") || "-"}
                        </p>
                      </div>
                      <div className="detailContent">
                        {placeHolderShimer && <CustomeShimer />}
                        <h5>{t("PRIORITY")}</h5>
                        <p>{stateNoticeMagment?.priority}</p>
                      </div>
                      <div className="detailContent">
                        {placeHolderShimer && <CustomeShimer />}
                        <h5>{t("SIGN_REQUIREDNOR")}</h5>
                        <p>
                          {stateNoticeMagment?.signrequired === true
                            ? "Yes"
                            : "No"}
                        </p>
                      </div>
                      <div
                        className={
                          stateNoticeMagment?.documenturl
                            ? "detailContent documentDesc"
                            : "detailContent"
                        }
                      >
                        {placeHolderShimer && <CustomeShimer />}
                        <h5 className={`${stateNoticeMagment?.description?.length >= 10 && !stateNoticeMagment?.description?.includes(' ')
                          ? 'breakeAll document-btnwrapper'
                          : 'wordBrake document-btnwrapper'
                          }`}>{t("DESCRIPTION")}</h5>
                        <p className={`${stateNoticeMagment?.description?.length >= 10 && !stateNoticeMagment?.description?.includes(' ')
                          ? 'breakeAll document-btnwrapper'
                          : 'wordBrake document-btnwrapper'
                          }`}>{stateNoticeMagment?.description}</p>
                      </div>
                    </div>
                  </Tab>
                </Tabs>
              </div>
            </Card.Body>
          </Card>
        </div>
      </React.Fragment>

      {/** * Renders a confirmation modal for deleting a user. * @param{" "}
      {{ string }} modalTitle - The title of the modal. * @param {{ string }}{" "}
      modalDescription - The description of the modal. * @param {{ boolean }}{" "}
      isUnderline - Determines if the modal title should be underlined. * @param{" "}
      {{ boolean }} showModal - Determines if the modal should be shown. *
      @param {{ string }} leftBtnTitle - The title of the left button. * @param{" "}
      {{ boolean }} isModelIcon - Determines if an icon should be displayed in
      the modal. * @param {{ string }} iconColor - The color of the icon. *
      @param {{ string }} iconCricle - The class name for the icon circle. * @
      */}
      <ConfirmModal
        modalTitle={t("DELETE")}
        modalDescription={t("ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_NOTICE")}
        isUnderline={false}
        showModal={showDeleteUserModal}
        leftBtnTitle={t("YES_DELETE")}
        isModelIcon={true}
        iconColor={"mdi mdi-delete-outline "}
        iconCricle={"iconCricleDelete"}
        leftBtnClass={"ok_Btn"}
        isFirstButton={true}
        isSecondButton={true}
        rightBtnClass="Border_Button"
        rightBtnTitle={t("NO_CANCEL")}
        onProcced={onDeleteUser}
        rightButtonClick={closeDeleteUserModal}
      />
      {/**
       * Renders a confirmation modal component for deactivating a category.
       * @param {{string}} className - The CSS class name for the modal.
       * @param {{boolean}} isModelIcon - Determines if an icon should be displayed in the modal.
       * @param {{function}} onHide - Callback function to handle hiding the modal.
       * @param {{string}} iconColor - The color of the icon in the modal.
       * @param {{string}} iconCricle - The CSS class name for the circle icon in the modal.
       * @param {{string}} modalTitle - The title of the modal.
       * @param {{string}} modalDescription - The description text of the modal.
       * @param {{boolean}} isUnderline - Determines if
       */}
      <ConfirmModal
        className={"Deactivate"}
        isModelIcon={true}
        onHide={onHide}
        iconColor={"mdi mdi-toggle-switch-off-outline "}
        iconCricle={"iconCricleDelete"}
        modalTitle={t("DEACTIVATE")}
        modalDescription={t("ARE_YOU_SURE_YOU_WANT_TO_DEACTIVATE_THIS_NOTICE")}
        isUnderline={false}
        showModal={showStatusModal}
        leftBtnTitle={t("YES_DEACTIVATE")}
        isFirstButton={true}
        leftBtnClass={"Deactivate-btn"}
        onProcced={onProcced}
        rightBtnClass="Cancel_Btn"
        isSecondButton={true}
        rightBtnTitle={t("NO_CANCEL")}
        rightButtonClick={closeDeactiveModal}
        loaderButtonShow={loaderButtonShow}
      />
    </>
  );
};

export default NoticeManagementDetailsNewComponent;
